import type {ReactElement, ReactNode} from 'react';
import React, {useState} from 'react';
import {Text, TextSize} from '@Components/text';
import {AuthInput} from '@Components/auth-input';
import {INPUT_FIELD_TYPE} from '@Components/input-field';
import {AUTH_TYPE, PASSWORD_PLACEHOLDER} from '@Libraries/login-signup-library';
import {Button, ButtonSize, ButtonType} from '@Components/button';
import {ClickableDiv} from '@Components/clickable-div';
import {noop} from '@Utils/general.util';
import {setIsConnectSSOAccountModal} from '@Components/login-page/login-page-reducer';
import type {VerifyUserEmailAndPasswordResponse} from '@Panels/connect-sso-account-panel/connect-sso-account-panel.types';
import {useUpdateAuthFormTypes} from '@Hooks/login-signup/useUpdateAuthFormTypes';
import {CONNECT_SSO_ACCOUNT_PANEL_ID} from '@Panels/connect-sso-account-panel';
import {closePanel} from '@Components/panel/panel-reducer';
import styles from './connect-account-login-form.module.scss';
import {useAppDispatch} from '@/hooks';
import {closeTwoFactorAuthenticationVerificationModal, openTwoFactorAuthenticationVerificationModal} from '@Modals/two-factor-authentication-verification-modal/two-factor-authentication-verification-modal.library';
import {TwoFactorVerificationUserActionType} from '@Panels/two-factor-authentication-verification-panel/two-factor-authentication-verification-panel.types';

interface ConnectAccountLoginFormProps {
  email: string;
  errorMessage: string;
  description: string;
  onConnectCallback: (email: string, password: string) => void;
}

export function ConnectAccountLoginForm({...props}: ConnectAccountLoginFormProps): ReactElement<any> {
  const [password, setPassword] = useState('');
  const [isPasswordInvalid, setIsPasswordInvalid] = useState(false);
  const [errorMessage, setErrorMessage] = useState(props.errorMessage);
  const [isLoading, setIsLoading] = useState(false);

  const {updateAuthTypes} = useUpdateAuthFormTypes();
  const dispatch = useAppDispatch();

  const isLoginPasswordInvalid = (): boolean => {
    return password.length === 0;
  };

  const handlePasswordInputChange = React.useCallback((txt: string) => {
    setPassword(txt.trim());
  }, []);

  const onSubmit = (): void => {
    setIsPasswordInvalid(isLoginPasswordInvalid());

    if (isLoginPasswordInvalid()) {
      return;
    }

    void verifyUserCredentials();
  };

  const onTwoFactorAuthenticationVerificationSuccess = (): void => {
    props.onConnectCallback(props.email, password);
    dispatch(closePanel(CONNECT_SSO_ACCOUNT_PANEL_ID));
    closeTwoFactorAuthenticationVerificationModal();
  };

  const verifyUserCredentials = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const response = (await window.PMW.writeLocal('authenticate/verifyUserEmailAndPassword', {
        email: props.email,
        password,
      })) as VerifyUserEmailAndPasswordResponse;

      if (response.match) {
        if (response.pending2FA) {
          openTwoFactorAuthenticationVerificationModal({
            onVerificationSuccessCallback: onTwoFactorAuthenticationVerificationSuccess,
            userActionType: TwoFactorVerificationUserActionType.LINK_SSO_ACCOUNT,
          });
        } else {
          props.onConnectCallback(props.email, password);
          dispatch(closePanel(CONNECT_SSO_ACCOUNT_PANEL_ID));
        }
      } else {
        setIsPasswordInvalid(true);
      }
    } catch (e) {
      setErrorMessage(window.i18next.t('pmwjs_something_wrong_contact_support', {supportLink: window.PMW.util.site_url('user/feedbackform')}));
    }
    setIsLoading(false);
  };

  const onForgotPasswordClick = (): void => {
    updateAuthTypes(AUTH_TYPE.FORGOT_PASSWORD);
    dispatch(setIsConnectSSOAccountModal());
  };

  /**
   * Reports a failed request, which is why it keeps the banner treatment. The reason the dialog opened
   * at all is ordinary body copy — see `getDescription`.
   */
  const getErrorMessage = (): ReactNode => {
    if (errorMessage === '') {
      return null;
    }
    return (
      <div className={`${styles.errorContainer} flex-center spacing-p-t-3 spacing-p-b-3 spacing-p-l-5 spacing-p-r-5`}>
        <Text size={TextSize.MD} val={errorMessage} className={styles.errorText} dangerouslySetInnerHTML />
      </div>
    );
  };

  const getDescription = (): ReactNode => {
    if (props.description === '') {
      return null;
    }
    return <Text className={styles.description} dangerouslySetInnerHTML size={TextSize.LG} val={props.description} />;
  };

  const getEmailInputField = (): ReactElement => {
    return (
      <div className={styles.emailFieldSlot}>
        <AuthInput
          disabled
          handleInput={noop}
          label={window.i18next.t('pmwjs_email')}
          onInputSubmit={noop}
          placeholder={props.email}
          type={INPUT_FIELD_TYPE.EMAIL}
          value={props.email}
        />
      </div>
    );
  };

  const getPasswordInputField = (): ReactElement => {
    return (
      <div className={styles.passwordFieldGroup}>
        <AuthInput
          enableFocusStyles
          errorText={window.i18next.t('pmwjs_incorrect_password')}
          handleInput={handlePasswordInputChange}
          label={window.i18next.t('pmwjs_password')}
          onInputSubmit={onSubmit}
          placeholder={PASSWORD_PLACEHOLDER}
          showError={isPasswordInvalid}
          type={INPUT_FIELD_TYPE.PASSWORD}
        />

        <ClickableDiv className={styles.forgotPassword} onClick={onForgotPasswordClick}>
          <Text className={styles.forgotPasswordLink} size={TextSize.MD} val={window.i18next.t('pmwjs_login_forgot_password')} />
        </ClickableDiv>
      </div>
    );
  };

  const getFormSubmitButton = (): ReactElement => {
    return <Button isFullWidth isLoading={isLoading} onClick={onSubmit} size={ButtonSize.MEDIUM} text={window.i18next.t('pmwjs_log_in')} type={ButtonType.PRIMARY} />;
  };

  return (
    <>
      {getErrorMessage()}
      <div className={styles.dialogBody}>
        {getDescription()}
        <div className={styles.formLayout}>
          {getEmailInputField()}
          {getPasswordInputField()}
          {getFormSubmitButton()}
        </div>
      </div>
    </>
  );
}
